import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import {
  Button,
  Typography,
  Modal,
  notification,
  Spin,
  Empty,
  Tooltip,
  ConfigProvider,
} from 'antd';
import {
  CopyOutlined,
  UpOutlined,
  DownOutlined,
  SearchOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash, faArrowsRotate, faPlus, faGripVertical } from '@fortawesome/free-solid-svg-icons';
import type { AppDispatch, RootState } from '../../../redux/store';
import {
  fetchCustomizations,
  createCustomization,
  updateCustomization,
  deleteCustomization,
  cloneCustomization,
  updateOptionSoldOut,
  setSelectedItem,
  setExpandedIndex,
  setPage,
  setShowCreateForm,
  setCustomizationData,
  setShowDeleteDialog,
  setCustomizationToDelete,
  setRefreshing,
  setSearchQuery,
  updateOptionInCustomization,
  reorderOptions,
  type Customization,
  type CustomizationData,
} from '../../../redux/Customizations';
import CustomizationSideBox from './CustomizationSideBox';
import CreateCustomizationDrawer from './CreateCustomizationDrawer';
import {
  Container,
  HeaderCard,
  Title,
  Subtitle,
  ControlsRow,
  LastUpdatedText,
  RefreshButton,
  CreateButton,
  SearchCard,
  StyledSearchInput,
  ListContainer,
  CustomizationList,
  CustomizationCard,
  CustomizationHeader,
  CustomizationInfo,
  CustomizationTitle,
  CustomizationMeta,
  ActionButtons,
  IconButton,
  DuplicateButton,
  DuplicateIconButton,
  ActionIconButton,
  ExpandedContent,
  OptionItem,
  OptionNameText,
  SoldOutButtonSmall,
  RelatedItemsSection,
  RelatedItemsTitle,
  RelatedItemText,
  EmptyContainer,
  PaginationText,
  FullWidthSpaceSmall,
  FlexContainerCenterPadding,
  StyledModal,
  TooltipContainer,
  TooltipHeader,
  TooltipInfoRow,
  TooltipLabel,
  TooltipValue,
  TooltipOptionsSection,
  TooltipOptionsTitle,
  TooltipOptionsList,
  TooltipOptionItem,
  TooltipSequenceBadge,
  TooltipOptionText,
} from './Styles';
import { CustomizationsProps } from './types';
import { useTheme } from '../../../context/ThemeContext';
import { getAntdThemeConfig } from '../../../utils/antdTheme';

const Customizations: React.FC<CustomizationsProps> = ({
  role,
  selectedCustomizationId,
  tabIndex,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const theme = useMemo<{ mode: 'light' | 'dark' }>(
    () => ({ mode: isDarkMode ? 'dark' : 'light' }),
    [isDarkMode]
  );

  // Redux state
  const {
    customizations,
    filteredCustomizations,
    selectedItem,
    loading,
    error,
    refreshing,
    lastUpdated,
    expandedIndex,
    page,
    hasMore,
    total,
    dataFetched,
    showCreateForm,
    submitting,
    customizationData,
    showDeleteDialog,
    customizationToDelete,
    searchQuery,
  } = useSelector((state: RootState) => state.customizations);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [isSearching, setIsSearching] = useState(false);
  const [customizationOrder, setCustomizationOrder] = useState<(number | string)[]>([]);
  useEffect(() => {
    setSearchInput(searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    const trimmedInput = searchInput.trim();
    const trimmedQuery = searchQuery.trim();

    if (trimmedInput === trimmedQuery) {
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    const debounceTimer = setTimeout(() => {
      dispatch(setSearchQuery(trimmedInput));
      dispatch(setPage(1));
      
      if (tabIndex === 5) {
        dispatch(fetchCustomizations({ pageNum: 1, search: trimmedInput })).finally(() => {
          setIsSearching(false);
        });
      } else {
        setIsSearching(false);
      }
    }, 500);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [searchInput, dispatch, tabIndex, searchQuery]);

  useEffect(() => {
    if (tabIndex === 5 && !dataFetched) {
      dispatch(fetchCustomizations({ pageNum: 1, search: searchQuery }));
    }
  }, [tabIndex, dataFetched, dispatch, searchQuery]);

  useEffect(() => {
    if (page > 1) {
      dispatch(fetchCustomizations({ pageNum: page, search: searchQuery }));
    }
  }, [page, dispatch, searchQuery]);

  useEffect(() => {
    const sortedCustomizations = [...filteredCustomizations]
      .sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0));
    
    const newOrder = sortedCustomizations.map((c) => c.id);
    setCustomizationOrder(newOrder);
  }, [filteredCustomizations]);

  useEffect(() => {
    if (selectedCustomizationId && customizations.length > 0) {
      const customization = customizations.find(
        (c) => c.id.toString() === selectedCustomizationId.toString()
      );
      if (customization) {
        dispatch(setSelectedItem(customization.id));
        dispatch(
          setCustomizationData({
            customization: customization.customization,
            options: customization.option,
            note: customization.note,
            max_quantity: customization.max_quantity,
            min_quantity: customization.min_quantity,
            multiple_selections: customization.multiple_selections,
            sold_out: Boolean(customization.sold_out),
            multiple_selections_quantity: customization.multiple_selections_quantity,
            related_items: customization.related_items || [],
          })
        );
      }
    }
  }, [selectedCustomizationId, customizations, dispatch]);

  useEffect(() => {
    const handleCustomizationUpdated = () => {
      dispatch(fetchCustomizations({ pageNum: 1, search: searchQuery }));
    };

    const handleCustomizationCreated = () => {
      dispatch(fetchCustomizations({ pageNum: 1, search: searchQuery }));
    };

    const handleOptionsReordered = (event: CustomEvent) => {
      const { customizationId, reorderedOptions } = event.detail;
      dispatch(reorderOptions({ customizationId, reorderedOptions }));
    };

    window.addEventListener('customizationUpdated', handleCustomizationUpdated);
    window.addEventListener('customizationCreated', handleCustomizationCreated);
    window.addEventListener('optionsReordered', handleOptionsReordered);

    return () => {
      window.removeEventListener('customizationUpdated', handleCustomizationUpdated);
      window.removeEventListener('customizationCreated', handleCustomizationCreated);
      window.removeEventListener('optionsReordered', handleOptionsReordered);
    };
  }, [dispatch, searchQuery]);

  const handleRefresh = useCallback(async () => {
    dispatch(setRefreshing(true));
    await dispatch(fetchCustomizations({ pageNum: 1, search: searchQuery }));
  }, [dispatch, searchQuery]);

  const handleCreate = useCallback(async () => {
    dispatch(setSelectedItem(null));
    dispatch(setShowCreateForm(true));

    const getNextSequenceNumber = () => {
      if (customizations.length === 0) return 1;
      const existingSequenceNumbers = customizations.map((c) => c.sequence_number || 0);
      const maxSequenceNumber = Math.max(...existingSequenceNumbers);
      return maxSequenceNumber + 1;
    };

    if (customizations.length === 0) {
      await dispatch(fetchCustomizations({ pageNum: 1, search: searchQuery }));
    }

    dispatch(
      setCustomizationData({
        customization: '',
        options: [],
        note: '',
        sequence_number: getNextSequenceNumber(),
        max_quantity: 0,
        min_quantity: 0,
        multiple_selections: false,
        sold_out: false,
        multiple_selections_quantity: 0,
        related_items: [],
      })
    );
  }, [customizations, dispatch, searchQuery]);

  const handleCreateSubmit = useCallback(async () => {
    if (!customizationData?.customization || customizationData.customization.trim() === '') {
      notification.error({
        message: 'Validation Error',
        description: 'Customization name is required.',
      });
      return;
    }

    try {
      const result = await dispatch(createCustomization(customizationData)).unwrap();
      notification.success({
        message: 'Success',
        description: 'Customization created successfully!',
      });
      
      window.dispatchEvent(
        new CustomEvent('customizationCreated', {
          detail: { customization: result },
        })
      );
      
      dispatch(setShowCreateForm(false));
      dispatch(setCustomizationData(null));
      dispatch(setSelectedItem(null));
      await handleRefresh();
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error || 'Failed to create customization.',
      });
    }
  }, [customizationData, dispatch, handleRefresh]);

  const handleItemClick = useCallback(
    (customization: Customization) => {
      dispatch(setShowCreateForm(false));
      dispatch(setSelectedItem(customization.id));
      dispatch(
        setCustomizationData({
          customization: customization.customization,
          options: customization.option,
          note: customization.note,
          max_quantity: customization.max_quantity,
          min_quantity: customization.min_quantity,
          multiple_selections: customization.multiple_selections,
          sold_out: Boolean(customization.sold_out),
          multiple_selections_quantity: customization.multiple_selections_quantity,
          related_items: customization.related_items || [],
        })
      );
      setIsEditModalOpen(true);
    },
    [dispatch]
  );

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    dispatch(setSelectedItem(null));
    dispatch(setCustomizationData(null));
  }, [dispatch]);

  const handleDeleteClick = useCallback(
    (customization: Customization) => {
      dispatch(setCustomizationToDelete(customization));
      dispatch(setShowDeleteDialog(true));
    },
    [dispatch]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!customizationToDelete) return;

    setIsDeleting(true);
    try {
      await dispatch(deleteCustomization(customizationToDelete.id)).unwrap();
      notification.success({
        message: 'Success',
        description: `"${customizationToDelete.customization}" has been deleted.`,
      });

      window.dispatchEvent(
        new CustomEvent('customizationDeleted', {
          detail: { customization: customizationToDelete },
        })
      );

      if (selectedItem === customizationToDelete.id) {
        dispatch(setSelectedItem(null));
        dispatch(setCustomizationData(null));
      }

      dispatch(setPage(1));
      await dispatch(fetchCustomizations({ pageNum: 1, search: searchQuery }));
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error || 'Failed to delete customization.',
      });
    } finally {
      setIsDeleting(false);
    }
  }, [customizationToDelete, selectedItem, dispatch, searchQuery]);

  const handleDeleteCancel = useCallback(() => {
    dispatch(setShowDeleteDialog(false));
    dispatch(setCustomizationToDelete(null));
  }, [dispatch]);

  const handleDuplicateClick = useCallback(
    async (customization: Customization) => {
      setDuplicatingId(customization.id);
      try {
        const result = await dispatch(cloneCustomization(customization.id)).unwrap();
        notification.success({
          message: 'Success',
          description: `"${customization.customization}" has been duplicated.`,
        });
        
        window.dispatchEvent(
          new CustomEvent('customizationCreated', {
            detail: { customization: result },
          })
        );
      } catch (error: any) {
        notification.error({
          message: 'Error',
          description: error || 'Failed to clone customization.',
        });
      } finally {
        setDuplicatingId(null);
      }
    },
    [dispatch]
  );

  const toggleAccordion = useCallback(
    (index: number) => {
      dispatch(setExpandedIndex(expandedIndex === index ? null : index));
    },
    [expandedIndex, dispatch]
  );

  const submitSoldOut = useCallback(
    async (option: any, updatedOption: any) => {
      try {
        await dispatch(updateOptionSoldOut({ option, updatedOption })).unwrap();
        dispatch(
          updateOptionInCustomization({
            customizationId: updatedOption.customization,
            optionId: option.id,
            sold_out: updatedOption.sold_out,
          })
        );
        notification.success({
          message: 'Success',
          description: 'Option updated successfully.',
        });
        await handleRefresh();
      } catch (error: any) {
        notification.error({
          message: 'Error',
          description: error || 'Failed to update option.',
        });
      }
    },
    [dispatch, handleRefresh]
  );

  // Handle reorder of customizations using @hello-pangea/dnd
  const handleDragEnd = useCallback(async (result: DropResult) => {
    const { source, destination, draggableId } = result;

    // If dropped outside the list
    if (!destination) {
      return;
    }

    // If dropped in the same position
    if (source.index === destination.index) {
      return;
    }

    // Create new order
    const newOrder = Array.from(customizationOrder);
    newOrder.splice(source.index, 1);
    newOrder.splice(destination.index, 0, draggableId as number | string);
    setCustomizationOrder(newOrder);

    try {
      // Collect all items that changed position with their new sequence numbers
      const updates: Promise<any>[] = [];
      const itemsToUpdate: { id: number | string; newSequence: number }[] = [];

      // Find which items changed position
      for (let i = Math.min(source.index, destination.index); i <= Math.max(source.index, destination.index); i++) {
        const id = newOrder[i];
        const oldIndex = customizationOrder.indexOf(id);

        // If position changed, add to updates
        if (oldIndex !== i) {
          itemsToUpdate.push({ id, newSequence: i });
        }
      }

      // Update all affected items in parallel
      for (const { id, newSequence } of itemsToUpdate) {
        const customization = filteredCustomizations.find((c) => c.id === id);
        if (customization) {
          updates.push(
            dispatch(
              updateCustomization({
                id: customization.id,
                data: {
                  sequence_number: newSequence,
                },
                type: 'default',
              })
            ).unwrap()
          );
        }
      }

      // Wait for all updates to complete
      if (updates.length > 0) {
        await Promise.all(updates);
      }

      notification.success({
        message: 'Success',
        description: 'Customizations reordered successfully.',
      });

      // No refresh needed - UI already updated optimistically with correct sequences!
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error || 'Unable to update the customization order.',
      });
    }
  }, [customizationOrder, filteredCustomizations, dispatch]);

  const handleSubmit = useCallback(
    async (submissionType: string, data: any) => {
      if (!selectedItem) return;

      try {
        if (submissionType === 'name') {
          if (!data?.customization || data.customization.trim() === '') {
            notification.error({
              message: 'Validation Error',
              description: 'Customization name cannot be empty.',
            });
            return;
          }

          const updated = await dispatch(
            updateCustomization({
              id: selectedItem,
              data: { customization: data.customization.trim() },
              type: 'name',
            })
          ).unwrap();
          notification.success({
            message: 'Success',
            description: 'Customization name updated successfully.',
          });
          
          window.dispatchEvent(
            new CustomEvent('customizationUpdated', {
              detail: { customization: updated },
            })
          );
          
          await handleRefresh();
        } else if (submissionType === 'options') {
          const updated = await dispatch(
            updateCustomization({
              id: selectedItem,
              data,
              type: 'options',
            })
          ).unwrap();
          notification.success({
            message: 'Success',
            description: 'Options updated successfully.',
          });
          
          window.dispatchEvent(
            new CustomEvent('customizationUpdated', {
              detail: { customization: updated },
            })
          );
          
          await handleRefresh();
          dispatch(setSelectedItem(null));
          dispatch(setCustomizationData(null));
        } else if (submissionType === 'addtoitems') {
          const updated = await dispatch(
            updateCustomization({
              id: selectedItem,
              data,
              type: 'addtoitems',
            })
          ).unwrap();
          notification.success({
            message: 'Success',
            description: 'Items updated successfully.',
          });
          
          window.dispatchEvent(
            new CustomEvent('customizationUpdated', {
              detail: { customization: updated },
            })
          );
          
          dispatch(setSelectedItem(null));
          dispatch(setCustomizationData(null));
        } else if (submissionType === 'customizationrules') {
          const updated = await dispatch(
            updateCustomization({
              id: selectedItem,
              data,
              type: 'customizationrules',
            })
          ).unwrap();
          notification.success({
            message: 'Success',
            description: 'Rules updated successfully.',
          });
          
          window.dispatchEvent(
            new CustomEvent('customizationUpdated', {
              detail: { customization: updated },
            })
          );
        }
      } catch (error: any) {
        notification.error({
          message: 'Error',
          description: error || 'Failed to update customization.',
        });
      }
    },
    [selectedItem, dispatch, handleRefresh]
  );

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
          <Container>
            <EmptyContainer>
              <Empty
                description={error}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
              <FlexContainerCenterPadding>
                  <Button type="primary" onClick={() => dispatch(fetchCustomizations({ pageNum: 1, search: searchQuery }))}>
                  Try Again
                </Button>
              </FlexContainerCenterPadding>
            </EmptyContainer>
          </Container>
        </ConfigProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderCard>
            <Title level={1}>Customizations</Title>
            <Subtitle>Set menu schedules and availability</Subtitle>

            <ControlsRow>
              <LastUpdatedText>
                Last updated: {lastUpdated || 'Never'}
                <RefreshButton
                  icon={<FontAwesomeIcon spin={refreshing} icon={faArrowsRotate} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />}
                  onClick={handleRefresh}
                  size="small"
                />
              </LastUpdatedText>

              {role !== 'staff' && (
                <CreateButton onClick={handleCreate}>
                  <FontAwesomeIcon icon={faPlus} style={{ marginRight: 8 }} />
                  Create Customization
                </CreateButton>
              )}
            </ControlsRow>
          </HeaderCard>
          <SearchCard>
            <StyledSearchInput
              placeholder="Search customizations"
              prefix={isSearching ? null : <SearchOutlined />}
              suffix={isSearching ? <Spin size="small" /> : null}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              allowClear
            />
          </SearchCard>
          <CreateCustomizationDrawer
            open={showCreateForm}
            onClose={() => {
              dispatch(setShowCreateForm(false));
              dispatch(setCustomizationData(null));
            }}
            onSubmit={handleCreateSubmit}
            submitting={submitting}
          />
          <ListContainer>
            <CustomizationList>
              {loading && page === 1 && filteredCustomizations.length === 0 ? (
                <FlexContainerCenterPadding>
                  <Spin size="large" />
                </FlexContainerCenterPadding>
              ) : refreshing ? (
                <FlexContainerCenterPadding>
                  <Spin size="large" />
                </FlexContainerCenterPadding>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="customizations-list">
                    {(provided, snapshot) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={{ listStyle: 'none', padding: 0, margin: 0 }}
                      >
                        {customizationOrder.map((id, mapIndex) => {
                          const customization = filteredCustomizations.find((c) => c.id === id);
                          if (!customization) return null;
                          const index = mapIndex;
                          return (
                            <Draggable key={id} draggableId={String(id)} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={{
                                    cursor: 'grab',
                                    listStyle: 'none',
                                    marginBottom: '12px',
                                    ...provided.draggableProps.style,
                                  }}
                                >
                        <CustomizationCard
                          isSelected={selectedItem === customization.id}
                          style={{ cursor: 'grab' }}
                        >
                          <CustomizationHeader>
                            <CustomizationInfo>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FontAwesomeIcon 
                                  icon={faGripVertical} 
                                  fontSize={16} 
                                  color={isDarkMode ? '#9ca3af' : '#6b7280'}
                                  style={{ cursor: 'grab', marginRight: '4px' }}
                                />
                                <Tooltip 
                                  title={
                                    <TooltipContainer>
                                      <TooltipHeader>
                                        <TooltipInfoRow>
                                          <TooltipLabel>Order:</TooltipLabel>
                                          <TooltipValue>{customization.sequence_number || 0}</TooltipValue>
                                        </TooltipInfoRow>
                                        <TooltipInfoRow>
                                          <TooltipLabel>Options:</TooltipLabel>
                                          <TooltipValue $color="#4fd1c7">{customization.option?.length || 0}</TooltipValue>
                                        </TooltipInfoRow>
                                        {customization.related_items && customization.related_items.length > 0 && (
                                          <TooltipInfoRow>
                                            <TooltipLabel>Related Items:</TooltipLabel>
                                            <TooltipValue $color="#f6ad55">{customization.related_items.length}</TooltipValue>
                                          </TooltipInfoRow>
                                        )}
                                      </TooltipHeader>
                                      
                                      {customization.option && customization.option.length > 0 && (
                                        <TooltipOptionsSection>
                                          <TooltipOptionsTitle>Options Order:</TooltipOptionsTitle>
                                          <TooltipOptionsList>
                                            {[...(customization.option || [])]
                                              .sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0))
                                              .map((opt, idx) => (
                                                <TooltipOptionItem key={idx}>
                                                  <TooltipSequenceBadge>
                                                    #{opt.sequence_number || idx + 1}
                                                  </TooltipSequenceBadge>
                                                  <TooltipOptionText>
                                                    {opt.option}
                                                  </TooltipOptionText>
                                                </TooltipOptionItem>
                                              ))}
                                          </TooltipOptionsList>
                                        </TooltipOptionsSection>
                                      )}
                                    </TooltipContainer>
                                  }
                                  overlayStyle={{ maxWidth: '380px' }}
                                  overlayInnerStyle={{
                                    backgroundColor: '#2d3748',
                                    borderRadius: '8px',
                                    padding: '12px 14px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                                  }}
                                >
                                  <InfoCircleOutlined 
                                    style={{ 
                                      fontSize: '14px', 
                                      color: isDarkMode ? '#9ca3af' : '#6b7280',
                                      cursor: 'help',
                                      marginRight: '4px'
                                    }} 
                                  />
                                </Tooltip>
                                <CustomizationTitle level={5}>{customization.customization}</CustomizationTitle>
                              </div>
                              <CustomizationMeta>
                                {customization.option?.length || 0} options
                                {customization.sequence_number !== undefined && (
                                  <span style={{ marginLeft: '8px', color: isDarkMode ? '#9ca3af' : '#6b7280' }}>
                                    • Order: {customization.sequence_number}
                                  </span>
                                )}
                              </CustomizationMeta>
                            </CustomizationInfo>

                            <ActionButtons>
                              <Tooltip title="Edit customization">
                                <ActionIconButton
                                  iconColor="#2D6CCA"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleItemClick(customization);
                                  }}
                                >
                                  <FontAwesomeIcon icon={faPen} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
                                </ActionIconButton>
                              </Tooltip>

                              <Tooltip title="Duplicate customization">
                                <DuplicateIconButton
                                  icon={duplicatingId === customization.id ? <Spin size="small" /> : <CopyOutlined />}
                                  loading={duplicatingId === customization.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDuplicateClick(customization);
                                  }}
                                />
                                <DuplicateButton
                                  icon={duplicatingId === customization.id ? <Spin size="small" /> : <CopyOutlined />}
                                  loading={duplicatingId === customization.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDuplicateClick(customization);
                                  }}
                                >
                                  Duplicate
                                </DuplicateButton>
                              </Tooltip>

                              <Tooltip title="Delete customization">
                                <ActionIconButton
                                  iconColor="#f5222d"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(customization);
                                  }}
                                >
                                  <FontAwesomeIcon icon={faTrash} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
                                </ActionIconButton>
                              </Tooltip>

                              <Tooltip title={expandedIndex === index ? 'Hide options' : 'Show options'}>
                                <IconButton
                                  icon={expandedIndex === index ? <UpOutlined /> : <DownOutlined />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleAccordion(index);
                                  }}
                                />
                              </Tooltip>
                            </ActionButtons>
                          </CustomizationHeader>

                  {expandedIndex === index && (
                    <ExpandedContent>
                      <FullWidthSpaceSmall direction="vertical" size="small">
                        {[...(customization.option || [])]
                          .sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0))
                          .map((option, optionIndex) => (
                            <OptionItem key={optionIndex}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                <OptionNameText>
                                  {option.sequence_number !== undefined && (
                                    <span style={{ 
                                      marginRight: '8px', 
                                      color: isDarkMode ? '#9ca3af' : '#6b7280',
                                      fontSize: '12px',
                                      fontWeight: 'bold'
                                    }}>
                                      #{option.sequence_number}
                                    </span>
                                  )}
                                  {option.option}
                                </OptionNameText>
                              </div>
                              <SoldOutButtonSmall
                                size="small"
                                danger={!option.sold_out}
                                onClick={() =>
                                  submitSoldOut(option, { ...option, sold_out: !option.sold_out })
                                }
                              >
                                {option.sold_out ? 'Set In Stock' : 'Set Sold Out'}
                              </SoldOutButtonSmall>
                            </OptionItem>
                          ))}

                        {customization.related_items && customization.related_items.length > 0 && (
                          <RelatedItemsSection>
                            <RelatedItemsTitle>
                              Related Items ({customization.related_items.length}):
                            </RelatedItemsTitle>
                            {customization.related_items.map((relatedItem, itemIndex) => (
                              <RelatedItemText key={itemIndex}>
                                •{' '}
                                {typeof relatedItem.item === 'object' && relatedItem.item?.item
                                  ? relatedItem.item.item
                                  : typeof relatedItem.item === 'string'
                                  ? relatedItem.item
                                  : relatedItem.name || 'Unknown Item'}
                              </RelatedItemText>
                            ))}
                          </RelatedItemsSection>
                        )}
                      </FullWidthSpaceSmall>
                    </ExpandedContent>
                              )}
                        </CustomizationCard>
                        </div>
                        )}
                      </Draggable>
                      );
                      })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </CustomizationList>

            <StyledModal
              open={isEditModalOpen && selectedItem !== null && customizationData !== null}
              onCancel={handleCloseEditModal}
              footer={null}
              width={800}
              maskClosable={false}
              destroyOnClose
              style={{ top: 20 }}
            >
              {selectedItem && customizationData && (
                <CustomizationSideBox
                  customizationData={customizationData}
                  handleInputChange={(field: string, value: any) => {
                    dispatch(
                      setCustomizationData({
                        ...customizationData,
                        [field]: value,
                      })
                    );
                  }}
                  handleDelete={() => {
                    const customization = customizations.find((c) => c.id === selectedItem);
                    if (customization) {
                      handleDeleteClick(customization);
                      handleCloseEditModal();
                    }
                  }}
                  handleDuplicate={async () => {
                    if (!selectedItem) return;
                    await handleDuplicateClick(
                      customizations.find((c) => c.id === selectedItem)!
                    );
                    handleCloseEditModal();
                  }}
                  handleSeeChanges={() => {}}
                  onClose={handleCloseEditModal}
                  isOpen={true}
                  handleSubmit={handleSubmit}
                  setCustomizationData={(data: CustomizationData | null) => {
                    dispatch(setCustomizationData(data));
                  }}
                  handleEdit={(name: string) => {
                    dispatch(
                      setCustomizationData({
                        ...customizationData,
                        customization: name,
                      })
                    );
                  }}
                  isNewCustomization={false}
                  selectedItem={selectedItem}
                  handleRefresh={handleRefresh}
                  width="100%"
                  isEditOption={false}
                  autoEditOptions={false}
                />
              )}
            </StyledModal>
          </ListContainer>

          {loading && page > 1 && (
            <FlexContainerCenterPadding>
              <Spin size="large" />
            </FlexContainerCenterPadding>
          )}

          {hasMore && filteredCustomizations.length > 0 && !loading && (
            <FlexContainerCenterPadding style={{ marginTop: 16 }}>
              <Button
                onClick={() => dispatch(setPage(page + 1))}
                loading={loading && page > 1}
                style={{
                  backgroundColor: '#2D6CCA',
                  borderColor: '#2D6CCA',
                  color: '#fff',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#1a4a8f';
                  e.currentTarget.style.borderColor = '#1a4a8f';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#2D6CCA';
                  e.currentTarget.style.borderColor = '#2D6CCA';
                }}
              >
                Load More (Showing {filteredCustomizations.length} of {total})
              </Button>
            </FlexContainerCenterPadding>
          )}

          {!hasMore && filteredCustomizations.length > 0 && (
            <FlexContainerCenterPadding style={{ marginTop: 16 }}>
              <PaginationText>
                Showing all {filteredCustomizations.length} of {total} customizations
              </PaginationText>
            </FlexContainerCenterPadding>
          )}

          <Modal
            open={showDeleteDialog}
            onCancel={handleDeleteCancel}
            onOk={handleDeleteConfirm}
            title="Delete Customization"
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ 
              danger: true,
              loading: isDeleting,
              icon: isDeleting ? <Spin size="small" /> : null
            }}
          >
            <Typography.Text>
              Are you sure you want to delete "{customizationToDelete?.customization}"? This action
              cannot be undone and will remove the customization from all associated items.
            </Typography.Text>
          </Modal>
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default React.memo(Customizations);

